import { useState } from 'react'
import { useNavigate } from 'react-router'

import { documentRoute, routes, surveyRoute } from '@/app/routes'
import { UploadForm } from '@/features/document-upload/UploadForm'
import { useDocuments, useRoadmap } from '@/shared/api/queries'
import type { Document, Evaluation } from '@/shared/api/queries'
import { formatFullDate, useDeadlineText } from '@/shared/format'
import { useLocalization, useTranslate } from '@/shared/i18n'
import type { TranslationKey } from '@/shared/i18n'
import {
  byUrgency,
  documentIcon,
  documentTypeLabel,
  evaluationsOf,
  urgencyOf,
} from '@/shared/model'
import type { Urgency } from '@/shared/model'
import {
  Button,
  Card,
  Chip,
  Empty,
  Skeleton,
  Stack,
  StateBlock,
  Text,
  TextLink,
  useToast,
} from '@/shared/ui'

const FILTERS = ['all', 'expiring', 'valid', 'failed'] as const

type Filter = (typeof FILTERS)[number]

export function statusLabel(status: Document['status']): TranslationKey {
  return `documents.status.${status}`
}

function urgencyOfDocument(document: Document): Urgency {
  return document.status === 'failed'
    ? 'critical'
    : urgencyOf({ deadline: document.expires_on, hard_deadline: true })
}

function keeps(document: Document, filter: Filter): boolean {
  const urgency = urgencyOfDocument(document)

  if (filter === 'failed') {
    return document.status === 'failed'
  }

  if (filter === 'expiring') {
    return urgency === 'critical' || urgency === 'attention'
  }

  if (filter === 'valid') {
    return document.status !== 'failed' && urgency !== 'critical' && urgency !== 'attention'
  }

  return true
}

function missingCount(evaluations: Evaluation[], documents: Document[]): number {
  const stored = new Set(documents.map((document) => document.type))

  return evaluations.filter(
    (item) =>
      item.status !== 'satisfied' &&
      (item.required_documents ?? []).some((type) => !stored.has(type)),
  ).length
}

function DocumentCard({ document, className }: { document: Document; className?: string }) {
  const t = useTranslate()
  const { language } = useLocalization()
  const deadline = useDeadlineText()

  const urgency = urgencyOfDocument(document)

  return (
    <li {...(className ? { className } : {})}>
      <Card to={documentRoute(document.id)} className={`tone--${urgency}`}>
        <Stack direction="row" justify="between" align="start" gap="sm">
          <Text as="span" role="title">
            {documentIcon(document.type)}
          </Text>
          <span className="dot" aria-hidden="true" />
        </Stack>
        <Stack gap="xs">
          <Text as="span" role="body-strong">
            {t(documentTypeLabel(document.type))}
          </Text>
          <Text as="span" role="eyebrow" tone="muted">
            {t('documents.valid_until')}
          </Text>
          <Text as="span" role="title">
            {document.expires_on
              ? formatFullDate(document.expires_on, language)
              : t('documents.no_expiry')}
          </Text>
          <Text as="span" role="label" tone="tone">
            {urgency === 'critical' || urgency === 'attention'
              ? (deadline(document.expires_on) ?? t(statusLabel(document.status)))
              : t(statusLabel(document.status))}
          </Text>
        </Stack>
      </Card>
    </li>
  )
}

export function DocumentsPage() {
  const t = useTranslate()
  const navigate = useNavigate()
  const [adding, setAdding] = useState(false)
  const [filter, setFilter] = useState<Filter>('all')

  const documents = useDocuments()
  const roadmap = useRoadmap()
  const toast = useToast()

  const evaluations = evaluationsOf(roadmap.data)
  const all = documents.data ?? []
  const expiring = all.filter((document) => {
    const urgency = urgencyOfDocument(document)

    return urgency === 'critical' || urgency === 'attention'
  }).length

  const shown = all
    .filter((document) => keeps(document, filter))
    .sort((a, b) => byUrgency(urgencyOfDocument(a), urgencyOfDocument(b)))
  const missing = missingCount(evaluations, all)

  return (
    <Stack gap="md">
      <Stack direction="row" justify="between" gap="sm">
        <Stack gap="xs">
          <Text as="h1" role="heading-2">
            {t('documents.title')}
          </Text>
          <Text role="label" tone="muted">
            {t('documents.summary', { count: all.length })}
            {expiring > 0 ? (
              <>
                {' · '}
                <Text as="span" role="label" tone="tone" className="tone--attention">
                  {t('documents.summary.expiring', { count: expiring })}
                </Text>
              </>
            ) : null}
          </Text>
        </Stack>
        {!adding ? (
          <Button size="sm" pill onClick={() => setAdding(true)}>
            + {t('app.add')}
          </Button>
        ) : null}
      </Stack>

      {adding ? (
        <UploadForm
          onUploaded={(analysis) => {
            setAdding(false)
            toast(t('toast.document.added'), 'success')
            void navigate(documentRoute(analysis.document.id))
          }}
          onQuotaExceeded={() => void navigate(routes.subscription)}
          onSurvey={(tag) => void navigate(surveyRoute(tag), { state: { back: routes.documents } })}
          onClose={() => setAdding(false)}
        />
      ) : null}

      {all.length > 0 ? (
        <Stack direction="row" gap="sm" wrap>
          {FILTERS.map((candidate) => (
            <Chip
              key={candidate}
              selected={filter === candidate}
              onClick={() => setFilter(candidate)}
            >
              {t(`documents.filter.${candidate}` as TranslationKey)}
            </Chip>
          ))}
        </Stack>
      ) : null}

      <StateBlock
        pending={documents.isPending}
        error={documents.error}
        onRetry={() => void documents.refetch()}
        skeleton={<Skeleton variant="card" rows={2} />}
      >
        {all.length === 0 ? (
          <Empty icon="🗂" title={t('documents.empty.title')} body={t('documents.empty')}>
            <Button pill onClick={() => setAdding(true)}>
              {t('documents.empty.action')}
            </Button>
          </Empty>
        ) : (
          <Stack gap="md">
            {shown.length === 0 ? (
              <Empty body={t('documents.empty.filter')} />
            ) : (
              <ul className="bento">
                {shown.map((document, index) => (
                  <DocumentCard
                    key={document.id}
                    document={document}
                    {...(index === 0 ? { className: 'bento__full' } : {})}
                  />
                ))}
              </ul>
            )}

            {missing > 0 ? (
              <Card flat className="card--dashed">
                <Text role="body-strong">{t('documents.missing.title')}</Text>
                <Text role="caption" tone="muted" measure>
                  {t('documents.missing.body', { count: missing })}
                </Text>
                <TextLink to={routes.documents}>{t('documents.add')}</TextLink>
              </Card>
            ) : null}
          </Stack>
        )}
      </StateBlock>
    </Stack>
  )
}
